import { useMeetOverlays } from '../../features/meet/MeetOverlaysProvider';
import { useMeetPermissionsContext } from '../../features/meet/MeetPermissionsProvider';
import { MeetBackgroundOption } from '../../features/meet/types';
import Button from '../../components/common/button';
import Modal from '../../components/common/modal/modal';
import MobilePopup from '../../components/common/mobile-popup/mobile-popap';
import { PermissionsModal } from '../../components/permissions-modal';
import { BackgroundModal } from '../../components/modals/backgroundModal';
import { MacDeckSelectModal } from '../../components/tools/mac-cards/mac-deck-select-modal';

interface MeetOverlaysProps {
  settingsModal: React.ReactNode;
  isPsychologist: boolean;
  backgrounds: MeetBackgroundOption[];
  showMobilePopup: boolean;
  showDesktopModal: boolean;
  showMobilePopupStartSupervisor: boolean;
  showDesktopModalStartSupervisor: boolean;
  sharingPersonName: string;
  onConfirmMacDeckModal: () => void;
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  onCloseInstruction: () => void;
  onCloseBackgroundModal: () => void;
  onBlurBackground: () => void;
  onSelectBackground: (src: string) => void;
  onRejectStopSupervisor: () => void;
  onConfirmStopSupervisor: () => void;
  onRejectStartSupervisorGroup: () => void;
  onConfirmStartSupervisorGroup: () => void;
  onConfirmScreenShare: () => void;
  onCancelScreenShare: () => void;
}

export function MeetOverlays({
  settingsModal,
  isPsychologist,
  backgrounds,
  showMobilePopup,
  showDesktopModal,
  showMobilePopupStartSupervisor,
  showDesktopModalStartSupervisor,
  sharingPersonName,
  onConfirmMacDeckModal,
  sendMacCardsOpen,
  onCloseInstruction,
  onCloseBackgroundModal,
  onBlurBackground,
  onSelectBackground,
  onRejectStopSupervisor,
  onConfirmStopSupervisor,
  onRejectStartSupervisorGroup,
  onConfirmStartSupervisorGroup,
  onConfirmScreenShare,
  onCancelScreenShare,
}: MeetOverlaysProps) {
  const overlays = useMeetOverlays();
  const permissions = useMeetPermissionsContext();

  return (
    <>
      {overlays.openSettings && settingsModal}
      {isPsychologist && (
        <MacDeckSelectModal
          isOpen={overlays.macDeckModalOpen}
          onClose={() => overlays.setMacDeckModalOpen(false)}
          onConfirm={onConfirmMacDeckModal}
          sendMacCardsOpen={sendMacCardsOpen}
        />
      )}
      {permissions.showPermissionsModal && (
        <PermissionsModal
          open={permissions.showPermissionsModal}
          onClose={() => permissions.setShowPermissionsModal(false)}
          onRequestPermissions={permissions.reRequestPermissions}
        />
      )}
      {overlays.showModalInstruct && (
        <div className="fixed top-0 left-0 px-5 w-full h-full flex flex-col items-center justify-center backdrop-blur-sm z-[99999999999]">
          <div className="w-full max-w-[435px] border-[2px] bg-white border-[#dd777b] p-[15px] rounded-[14px] flex flex-col gap-[10px]">
            <p>{overlays.showModalInstruct}</p>
            <Button
              onClick={onCloseInstruction}
              className="!py-[10px] flex items-center justify-center !h-[34px] !text-[13px]"
            >
              Понятно
            </Button>
          </div>
        </div>
      )}
      <BackgroundModal
        isOpen={overlays.showBackgroundModal}
        onClose={onCloseBackgroundModal}
        onBlur={onBlurBackground}
        backgrounds={backgrounds}
        onSelect={onSelectBackground}
      />
      {showMobilePopup && (
        <MobilePopup
          acceptText="Продолжить"
          rejectText="Остановить"
          title="Остановить ИИ-супервизора?"
          description="Отключение остановит работу инструмента и анализ сессии. "
          onCancel={onRejectStopSupervisor}
          onRequest={onConfirmStopSupervisor}
        />
      )}
      {showDesktopModal && (
        <Modal
          title="Остановить ИИ-супервизора?"
          isConfirmCloseOpen={true}
          cancelCloseTool={onRejectStopSupervisor}
          confirmCloseTool={onConfirmStopSupervisor}
          confirmMessage="Отключение остановит работу инструмента и анализ сессии."
          cancelText="Остановить"
          nonCancelText="Продолжить"
        />
      )}
      {overlays.showScreenShareConflictModal && (
        <div className="fixed top-0 left-0 px-5 w-full h-full flex flex-col items-center justify-center backdrop-blur-sm z-[99999999999]">
          <div
            className="w-full max-w-[600px] bg-white flex flex-col"
            style={{
              paddingTop: '40px',
              paddingBottom: '40px',
              paddingLeft: '40px',
              paddingRight: '40px',
              borderRadius: '20px',
            }}
          >
            <h2
              className="text-[#111026] mb-3"
              style={{
                fontFamily: 'Comfortaa',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
                letterSpacing: '0%',
              }}
            >
              {sharingPersonName} уже показывает экран
            </h2>
            <p
              className="mb-6"
              style={{
                fontFamily: 'Manrope',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '135%',
                letterSpacing: '0%',
                color: '#2C3038',
                marginBottom: '24px',
              }}
            >
              Если вы начнете показ, текущая демонстрация будет остановлена. Продолжить?
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                className="w-full h-[52px] rounded-[16px] text-white bg-redBase border-2 border-redBase hover:border-redBaseWithBlur20 hover:bg-redBaseWithBlur20 active:border-blackBase active:bg-redBase flex justify-center items-center cursor-pointer"
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '135%',
                  letterSpacing: '0%',
                }}
                onClick={onConfirmScreenShare}
              >
                Начать свой показ
              </button>
              <button
                type="button"
                className="w-full h-[52px] rounded-[16px] text-greyBlue bg-greyLightBlueLight border-2 border-greyLightBlueLight hover:bg-blackBase hover:border-blackBase hover:text-white active:text-greyBlue active:border-blackBase active:bg-greyLightBlueLight flex justify-center items-center cursor-pointer"
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '135%',
                  letterSpacing: '0%',
                }}
                onClick={onCancelScreenShare}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {showMobilePopupStartSupervisor && (
        <MobilePopup
          acceptText="Включить"
          rejectText="Отмена"
          title="Включить ИИ-супервизор?"
          description="Запись групповой сессии будет анализироваться ИИ-супервизором. Участникам запрашивать разрешение не требуется."
          onCancel={onRejectStartSupervisorGroup}
          onRequest={onConfirmStartSupervisorGroup}
        />
      )}
      {showDesktopModalStartSupervisor && (
        <Modal
          title="Включить ИИ-супервизор?"
          isConfirmCloseOpen={true}
          cancelCloseTool={onRejectStartSupervisorGroup}
          confirmCloseTool={onConfirmStartSupervisorGroup}
          confirmMessage="Запись групповой сессии будет анализироваться ИИ-супервизором. Участникам запрашивать разрешение не требуется."
          cancelText="Включить"
          nonCancelText="Отмена"
        />
      )}
    </>
  );
}
